import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Activity, Heart, Users, CheckCircle, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

export const FamilyPhysicianServices: React.FC = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);

  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const services = [
    {
      title: "Preventive Care",
      description: "Our preventive care services focus on maintaining your health and preventing illness before it occurs. Regular check-ups, screenings, and immunizations help detect potential health issues early when they're most treatable.",
      details: [
        "Annual physical examinations",
        "Health risk assessments",
        "Immunizations for children and adults",
        "Cancer screenings (breast, colon, prostate, skin, etc.)",
        "Cardiovascular disease screening",
        "Diabetes screening",
        "Osteoporosis screening",
        "Preventive health counseling"
      ],
      icon: <Shield className="h-8 w-8 text-primary-600" />
    },
    {
      title: "Chronic Disease Management",
      description: "We provide comprehensive care for patients with chronic conditions, helping you manage your health effectively and improve your quality of life. Our approach includes regular monitoring, medication management, and lifestyle counseling.",
      details: [
        "Diabetes management",
        "Hypertension (high blood pressure) treatment",
        "Heart disease management",
        "Asthma and COPD care",
        "Thyroid disorders",
        "Arthritis treatment",
        "Depression and anxiety management",
        "Medication management and optimization"
      ],
      icon: <Activity className="h-8 w-8 text-primary-600" />
    },
    {
      title: "Women's Health",
      description: "Our women's health services address the unique healthcare needs of women at every stage of life. We provide comprehensive care from adolescence through menopause and beyond.",
      details: [
        "Well-woman exams",
        "Pap smears and cervical cancer screening",
        "Breast health and mammogram referrals",
        "Contraception counseling and management",
        "Menopause management",
        "Osteoporosis prevention and treatment",
        "Prenatal care and coordination with OB/GYN",
        "Treatment for common gynecological issues"
      ],
      icon: <Heart className="h-8 w-8 text-primary-600" />
    },
    {
      title: "Pediatric Care",
      description: "We provide comprehensive healthcare for children from birth through adolescence. Our child-friendly approach makes visits comfortable for young patients while ensuring they receive the care they need to grow and develop healthily.",
      details: [
        "Well-child visits and development monitoring",
        "Immunizations and vaccine counseling",
        "School and sports physicals",
        "Treatment of common childhood illnesses",
        "Growth and development assessment",
        "Behavioral and mental health screening",
        "Nutrition counseling",
        "Adolescent healthcare"
      ],
      icon: <Users className="h-8 w-8 text-primary-600" />
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Comprehensive healthcare services for your entire family, from preventive care to chronic disease management.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Family Care</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              At Family Physician Office, we provide a wide range of services to meet all your healthcare needs under one roof.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-md">
                <div className="mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{service.description}</p>
                <button 
                  onClick={() => toggleAccordion(index)}
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors flex items-center text-sm"
                >
                  Learn More
                  <ArrowRight className="ml-1 h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Detailed Service Information</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Click on each service to learn more about what we offer.
            </p>
          </div>
          
          <div className="space-y-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  className="flex justify-between items-center w-full p-6 text-left focus:outline-none"
                  onClick={() => toggleAccordion(index)}
                >
                  <div className="flex items-center">
                    <div className="mr-4">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{service.title}</h3>
                  </div>
                  {activeAccordion === index ? (
                    <ChevronUp className="h-5 w-5 text-primary-600" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-primary-600" />
                  )}
                </button>
                
                {activeAccordion === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">Services Include:</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {service.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-center text-gray-600">
                            <CheckCircle className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Schedule Your Visit?</h2>
          <p className="text-xl text-primary-100 mb-8">
            We're accepting new patients and would be honored to care for you and your family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/templates/family-physician#appointment" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Book an Appointment
            </Link>
            <Link to="/templates/family-physician#contact" className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};